'use client';

import H2 from '@/components/typography/h2';
import H3 from '@/components/typography/h3';
import Label from '@/components/typography/label';
import P from '@/components/typography/p';
import { Button } from '@/components/ui/button';
import { CopiableEmail } from '@/components/ui/copiable';
import { RichText } from '@/components/ui/rich-text';
import { Vacature } from '@/payload-types';
import {
    SerializedEditorState,
    SerializedLexicalNode,
} from '@payloadcms/richtext-lexical/lexical';
import { motion, useScroll, useTransform } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Dot } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type StickyCardScrollVacancyProps = {
    vacancy: Vacature;
};

export default function StickyCardScroll({
    vacancy,
}: StickyCardScrollVacancyProps) {
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end end'],
    });

    const coffeeRef = useRef<HTMLDivElement | null>(null);
    gsap.registerPlugin(ScrollTrigger);

    const getRichText = (key: keyof Vacature) => {
        const value = vacancy[key];
        if (typeof value === 'object' && value !== null) {
            return (
                <RichText
                    data={value as SerializedEditorState<SerializedLexicalNode>}
                />
            );
        }
        return null;
    };

    const wildcardContent = getRichText('wildcard');
    const wildcardContentIsValid =
        !!wildcardContent?.props?.data?.root?.children?.[0]?.children?.length;

    const sections = useMemo(() => {
        const baseSections = [
            {
                title: t('cardScroll.about.title'),
                content: <P>{t('cardScroll.about.text')}</P>,
            },
            {
                title: t('cardScroll.role'),
                key: 'rol',
            },
            {
                title: t('cardScroll.profile'),
                key: 'profiel',
            },
            {
                title: t('cardScroll.offering'),
                key: 'aanbod',
            },
        ];

        if (wildcardContentIsValid) {
            baseSections.push({ title: 'Extra', key: 'wildcard' });
        }

        baseSections.push({ title: 'cta', key: 'cta' });

        return baseSections;
    }, [vacancy, wildcardContentIsValid]);

    const total = sections.length;

    if (typeof window === 'undefined')
        return <div className="bg-beige h-screen w-full" />;

    return (
        <div
            className="relative flex"
            style={{ minHeight: `${sections.length * 100}vh` }}
            ref={ref}
        >
            {/* Left column */}
            <div className="border-primary relative w-[42.25%] border-b pb-40">
                <div className="sticky top-1/3 mb-[350px]">
                    <div className="flex justify-center">
                        <div className="flex w-[424px] flex-col gap-5">
                            <div>
                                <Label>{t('title')}</Label>
                            </div>
                            <div className="flex gap-5">
                                <div className="border-primary rounded-full border px-3 py-1">
                                    <div className="flex items-center gap-2">
                                        <Dot className="bg-primary h-2.5 w-2.5 rounded-full lg:h-3.5 lg:w-3.5" />
                                        <Label>{vacancy.level}</Label>
                                    </div>
                                </div>
                                <div className="border-primary rounded-full border px-3 py-1">
                                    <div className="flex items-center gap-2">
                                        <Dot className="bg-primary h-2.5 w-2.5 rounded-full lg:h-3.5 lg:w-3.5" />
                                        <Label>{vacancy.uren}</Label>
                                    </div>
                                </div>
                            </div>
                            <H2>{vacancy.titel}</H2>
                            <H3>{vacancy['pay-off']}</H3>
                        </div>
                    </div>

                    {/* Animated coffee image */}
                    <div ref={coffeeRef} className="absolute top-[100%]">
                        <Image
                            src="/images/stickers/coffe.png"
                            alt="coffe"
                            height={550}
                            width={550}
                        />
                    </div>
                </div>
            </div>

            {/* Right column */}
            <div className="relative w-[57.75%]">
                <div className="sticky top-0 h-screen">
                    {sections.slice(0, 1).map((section, i) => (
                        <div
                            key={i}
                            className={`border-primary bg-beige absolute left-0 flex h-screen w-full items-center justify-center overflow-auto border-l`}
                        >
                            <VacancySection
                                vacancy={vacancy}
                                section={section}
                                getRichText={getRichText}
                            />
                        </div>
                    ))}
                    {sections.slice(1).map((section, i) => (
                        <MotionVacancySection
                            key={i}
                            total={total}
                            scrollYProgress={scrollYProgress}
                            i={i}
                            vacancy={vacancy}
                            section={section}
                            getRichText={getRichText}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function MotionVacancySection({
    total,
    scrollYProgress,
    i,
    vacancy,
    section,
    getRichText,
}: {
    total: number;
    scrollYProgress: ReturnType<typeof useScroll>['scrollYProgress'];
    i: number;
    vacancy: Vacature;
    section: { title: string; content?: React.ReactNode; key?: string };
    getRichText: (key: keyof Vacature) => React.ReactNode | null;
}) {
    const index = i + 1;
    const step = 1 / (total - 1);
    const start = (index - 1) * step;
    const end = index * step;
    const y = useTransform(scrollYProgress, [start, end], ['100%', '0%']);

    return (
        <motion.div
            key={index}
            className={`border-primary bg-beige absolute left-0 flex h-screen w-full items-center justify-center overflow-auto ${
                index === 4 || index === 5
                    ? 'border-t border-b border-l'
                    : 'border-t border-l'
            }`}
            style={{ y }}
        >
            <VacancySection
                vacancy={vacancy}
                section={section}
                getRichText={getRichText}
            />
        </motion.div>
    );
}

function VacancySection({
    vacancy,
    section,
    getRichText,
}: {
    vacancy: Vacature;
    section: { title: string; content?: React.ReactNode; key?: string };
    getRichText: (key: keyof Vacature) => React.ReactNode | null;
}) {
    const { t } = useTranslation();

    return (
        <div className="relative flex max-h-full max-w-[769px] flex-col gap-5 px-5 py-20">
            {section.key !== 'cta' ? (
                <>
                    {section.title && <H3>{section.title}</H3>}
                    {section.content ? (
                        section.content
                    ) : (
                        <P className="pb-20">
                            {getRichText(section.key as keyof Vacature)}
                            {section.key === 'aanbod' && (
                                <Image
                                    src="/images/stickers/goodfood.png"
                                    alt="aanbod"
                                    height={125}
                                    width={200}
                                    className="absolute -top-15 right-[20%]"
                                />
                            )}
                        </P>
                    )}
                </>
            ) : (
                <div className="flex flex-col gap-[3.88rem]">
                    <H2 className="text-center">
                        {t('cardScroll.cta')}&nbsp;
                        <CopiableEmail email="merle@foodcabinet.nl" />
                        &nbsp;
                        {t('cardScroll.cta2')}
                    </H2>
                    {vacancy.isExterneVacature && (
                        <Link
                            target="_blank"
                            className="mx-auto w-fit"
                            href={vacancy.externeVacatureLink as string}
                        >
                            <Button className="px-6 py-5">
                                <Label variant="button">
                                    {t('cardScroll.apply')}
                                </Label>
                            </Button>
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}
